import { ViewRequest, getEligibilityBasedQueryKey, getViewRequest, request } from "@/App/api/request";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";
import {
  MemberDocumentList,
  MemberDocumentRequest,
  PlanDocumentList,
  PlanDocumentRequest,
  PresignedDownloadUrl,
} from "@/App/types/documents";
import { useContext } from "react";
import { MemberEligibilityContext } from "../context/MemberEligibilityContext";
import { useQuery } from "@tanstack/react-query";
import { AuthenticationContext } from "../context";

export const getMemberDocuments = async (authToken: string, body?: Partial<ViewRequest>) => {
  try {
    const response = (await request<typeof body, MemberDocumentList>(
      {
        endpoint: "/document_attachment/list",
        body: { ...(body || {}) },
      },
      authToken,
    )) as MemberDocumentList;
    return response;
  } catch (err) {
    logEvent(ANALYTIC_EVENTS.ERRORS.getMemberDocuments);
    throw err;
  }
};

export const useListMemberDocumentsQuery = (personCode?: string) => {
  const { isViewingDataholder, memberEligibility } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return getMemberDocuments(await getAuthToken(), { ...getViewRequest(memberEligibility) });
      } else {
        return getMemberDocuments(await getAuthToken(), {
          view_external_member_id: memberEligibility.member.external_member_id,
          view_client_id: memberEligibility.plan.client_id,
          view_person_code: personCode,
        });
      }
    },
    queryKey: ["memberDocumentList", ...getEligibilityBasedQueryKey(memberEligibility, personCode)],
  });

  return query;
};

export const getPlanDocuments = async (authToken: string, body?: Partial<ViewRequest>) => {
  try {
    const response = (await request<typeof body, PlanDocumentList>(
      {
        endpoint: "/member/document/list",
        body: { ...(body || {}) },
      },
      authToken,
    )) as PlanDocumentList;
    return response;
  } catch (err) {
    logEvent(ANALYTIC_EVENTS.ERRORS.getPlanDocuments);
    throw err;
  }
};

export const useListPlanDocumentsQuery = () => {
  const { isViewingDataholder, memberEligibility } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return getPlanDocuments(await getAuthToken(), { ...getViewRequest(memberEligibility) });
      } else {
        return getPlanDocuments(await getAuthToken());
      }
    },
    queryKey: ["planDocumentList", ...getEligibilityBasedQueryKey(memberEligibility)],
  });

  return query;
};

export const getMemberDocumentUrl = async (authToken: string, data: MemberDocumentRequest) => {
  try {
    const response = (await request<MemberDocumentRequest, PresignedDownloadUrl>(
      {
        endpoint: "/document_attachment/url_for_download",
        body: data,
      },
      authToken,
    )) as PresignedDownloadUrl;
    return response;
  } catch (err) {
    logEvent(ANALYTIC_EVENTS.ERRORS.getMemberDocumentUrl);
    throw err;
  }
};

export const getPlanDocumentUrl = async (authToken: string, data: PlanDocumentRequest) => {
  try {
    const response = (await request<PlanDocumentRequest, PresignedDownloadUrl>(
      {
        endpoint: "/member/document/url_for_download",
        body: data,
      },
      authToken,
    )) as PresignedDownloadUrl;
    return response;
  } catch (err) {
    logEvent(ANALYTIC_EVENTS.ERRORS.getPlanDocumentUrl);
    throw err;
  }
};
